import type { GenericMeasure } from 'safe-units';
import * as d3 from 'd3';
import { bits, bytes, Measure, seconds } from 'safe-units';
import { match } from 'ts-pattern';

export const milliseconds = Measure.of(1 / 1000, seconds);

export const kilobits = Measure.of(1000, bits, 'kb');
export const megabits = Measure.of(1000, kilobits, 'Mb');
export const gigabits = Measure.of(1000, megabits, 'Gb');
export const terabits = Measure.of(1000, gigabits, 'Tb');

/* eslint-enable @typescript-eslint/naming-convention */

export const kilobytes = Measure.of(1000, bytes, 'kB');
export const megabytes = Measure.of(1000, kilobytes, 'MB');
export const gigabytes = Measure.of(1000, megabytes, 'GB');
export const terabytes = Measure.of(1000, gigabytes, 'TB');
export const petabytes = Measure.of(1000, terabytes, 'PB');

export const bitsPerSecond = bits.per(seconds).withSymbol('bps');
export const kilobitsPerSecond = kilobits.per(seconds).withSymbol('kbps');
export const megabitsPerSecond = megabits.per(seconds).withSymbol('Mbps');
export const gigabitsPerSecond = gigabits.per(seconds).withSymbol('Gbps');
export const terabitsPerSecond = terabits.per(seconds).withSymbol('Tbps');

/* eslint-enable @typescript-eslint/naming-convention */

export const bytesPerSecond = bytes.per(seconds).withSymbol('Bps');
export const kilobytesPerSecond = kilobytes.per(seconds).withSymbol('kBps');
export const megabytesPerSecond = megabytes.per(seconds).withSymbol('MBps');
export const gigabytesPerSecond = gigabytes.per(seconds).withSymbol('GBps');
export const terabytesPerSecond = terabytes.per(seconds).withSymbol('TBps');

export interface DataRate extends GenericMeasure<number, { memory: '1'; time: '-1' }> {}

export const formatFixed = <T extends GenericMeasure<number, any>>(
  value: T,
  unit: T,
  precision: number = 0,
  numberFormat: string = '~f',
): string => `${d3.format(`,.${precision}${numberFormat}`)(value.over(unit).value)} ${unit.symbol}`;

/**
 * Formats a data rate using a suitable unit based in **bits**. For example,
 * if the value is less than 2kb/s, it will be formatted using bits/s; if less
 * than 2Mb/s, then kb/s; and so on.
 *
 * @param rate The data rate
 * @param quantity The quantity that the data rate is measured in
 * @param precision The number of decimal places to display
 */
export const formatDataRateBits = (
  rate: number,
  quantity: DataRate,
  precision: number = 0,
  numberFormat: string = '~f',
) =>
  match(Measure.of(rate, quantity))
    .when(
      (value) => value.lt(kilobitsPerSecond),
      (value) => formatFixed(value, bitsPerSecond, 0, numberFormat),
    )
    .when(
      (value) => value.lt(megabitsPerSecond.scale(1)),
      (value) => formatFixed(value, kilobitsPerSecond, precision, numberFormat),
    )
    .when(
      (value) => value.lt(gigabitsPerSecond),
      (value) => formatFixed(value, megabitsPerSecond, precision, numberFormat),
    )
    .when(
      (value) => value.lt(terabitsPerSecond),
      (value) => formatFixed(value, gigabitsPerSecond, precision, numberFormat),
    )
    .otherwise((value) => formatFixed(value, quantity, precision, numberFormat));

/**
 * Formats a data rate using a suitable unit based in **bytes**. For example,
 * if the value is less than 2kB/s, it will be formatted using bytes/s; if less
 * than 2MB/s, then kB/s; and so on.
 *
 * @param rate The data rate
 * @param quantity The quantity that the data rate is measured in
 * @param precision The number of decimal places to display
 */
export const formatDataRateBytes = (rate: number, quantity: DataRate, precision: number = 0) =>
  match(Measure.of(rate, quantity))
    .when(
      (value) => value.lt(kilobytesPerSecond),
      (value) => formatFixed(value, bytesPerSecond, 0),
    )
    .when(
      (value) => value.lt(megabytesPerSecond),
      (value) => formatFixed(value, kilobytesPerSecond, precision),
    )
    .when(
      (value) => value.lt(gigabytesPerSecond),
      (value) => formatFixed(value, megabytesPerSecond, precision),
    )
    .when(
      (value) => value.lt(terabytesPerSecond),
      (value) => formatFixed(value, gigabytesPerSecond, precision),
    )
    .otherwise((value) => formatFixed(value, quantity, precision));

export const formatBytes = (number: number, precision: number = 0) =>
  match(Measure.of(number, bytes))
    .when(
      (value) => value.lt(kilobytes),
      (value) => formatFixed(value, bytes, 0),
    )
    .when(
      (value) => value.lt(megabytes),
      (value) => formatFixed(value, kilobytes, precision),
    )
    .when(
      (value) => value.lt(gigabytes),
      (value) => formatFixed(value, megabytes, precision),
    )
    .when(
      (value) => value.lt(terabytes),
      (value) => formatFixed(value, gigabytes, precision),
    )
    .when(
      (value) => value.lt(petabytes),
      (value) => formatFixed(value, terabytes, precision),
    )
    .otherwise((value) => formatFixed(value, bytes, precision));
